/**
 * Cohort labels for Excel export "User Role" column.
 * Matching is case-insensitive on participant / submission email.
 */

import { localYmdToStartDate } from '../utils/exportDateFilter';

export const PILOT_1_USER_ROLE = 'Pilot- 1 User';
export const PILOT_2_USER_ROLE = 'Pilot- 2 User';
export const LIVE_LMS_USER_ROLE = 'Live LMS User';

/** Inclusive start of Pilot 2: after 11 Aug 2026 → from 12 Aug 2026 local midnight. */
export const PILOT_2_FROM_YMD = '2026-08-12';

/** Normalized (lowercase) emails for Pilot- 1 User role. */
export const PILOT_1_USER_EMAILS = new Set(
  [
    'ilaahinoor@outlook.com',
    'kaur_sukhleen_5@outlook.com',
    'lhamokinley@outlook.com',
    'mehwish161khan@gmail.com',
    'myra_gulwani@outlook.com',
    'nagmahaider@outlook.com',
    'rumarifat6@gmail.com',
    'saharsheikh207@gmail.com',
    'sanjam_kapoor@outlook.com',
    'shambhavi.rege1@gmail.com',
    'tharushidilan@outlook.com',
    'yaminijoshi185@gmail.com',
  ].map((e) => e.toLowerCase())
);

/** Normalized (lowercase) emails for Live LMS User role. */
export const LIVE_LMS_USER_EMAILS = new Set(
  [
    'simrangill234@hotmail.com',
    'vnc.1173@gmail.com',
    'muskanwadhwaa@gmail.com',
    'urmee.kohinur@gmail.com',
    'tharani754@gmail.com',
    'jotibains@hotmail.com',
    'ravneetkaur1703@gmail.com',
    'sadaf.adnan@lotusstemm.org',
    'wasifa.99@hotmail.com',
    'twadhwa1998@gmail.com',
    'joseph.jisha.88@gmail.com',
    'thegrubers@charter.net',
    'manpreetkaur@ucalgary.ca',
    'sara.enjamoori@stantec.com',
    'cyk733@gmail.com',
    'smyna.622@gmail.com',
    'rollybhasin@gmail.com',
    'jithamala.caldera@gmail.com',
    'thilupa.b@gmail.com',
    'lal.jan@circleinternational.ca',
  ].map((e) => e.toLowerCase())
);

function isEmptyDateValue(value) {
  if (value == null) return true;
  if (typeof value === 'string' && !value.trim()) return true;
  return false;
}

/** Normalize Auth / Firestore date fields to epoch ms. */
export function exportDateValueToMillis(value) {
  if (isEmptyDateValue(value)) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      try {
        const t = value.toDate().getTime();
        return Number.isNaN(t) ? null : t;
      } catch {
        return null;
      }
    }
    if (typeof value.seconds === 'number' && Number.isFinite(value.seconds)) {
      return value.seconds * 1000;
    }
  }
  return null;
}

/**
 * Registration instant for cohorting: Auth created at, else Profile synced at.
 * @param {{ authCreatedAt?: unknown, exportAuthSyncedAt?: unknown } | null | undefined} row
 * @returns {number | null}
 */
export function resolveExportRegistrationMillis(row) {
  if (!row) return null;
  return exportDateValueToMillis(row.authCreatedAt) ?? exportDateValueToMillis(row.exportAuthSyncedAt);
}

/**
 * @param {string | null | undefined} email
 * @param {{ authCreatedAt?: unknown, exportAuthSyncedAt?: unknown } | null | undefined} [row]
 * @returns {typeof PILOT_1_USER_ROLE | typeof PILOT_2_USER_ROLE | typeof LIVE_LMS_USER_ROLE}
 */
export function resolveExportUserRole(email, row) {
  const normalized = String(email || '').trim().toLowerCase();
  if (normalized && LIVE_LMS_USER_EMAILS.has(normalized)) {
    return LIVE_LMS_USER_ROLE;
  }

  const registeredMs = resolveExportRegistrationMillis(row);
  const pilot2FromMs = localYmdToStartDate(PILOT_2_FROM_YMD).getTime();
  if (registeredMs != null && registeredMs >= pilot2FromMs) {
    return PILOT_2_USER_ROLE;
  }

  if (normalized && PILOT_1_USER_EMAILS.has(normalized)) {
    return PILOT_1_USER_ROLE;
  }

  return PILOT_1_USER_ROLE;
}
